import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "section" | "li" | "article" | "figure";
}) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.08 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const Comp = Tag;

  return (
    <Comp
      ref={ref as never}
      className={`reveal ${className}`}
      style={
        {
          opacity: shown ? 1 : 0,
          transform: shown ? "none" : "translate3d(0, 34px, 0)",
          filter: shown ? "blur(0)" : "blur(8px)",
          transition:
            "opacity 1s var(--ease-silk), transform 1s var(--ease-silk), filter 1s var(--ease-silk)",
          transitionDelay: `${delay}ms`,
        } as CSSProperties
      }
    >
      {children}
    </Comp>
  );
}
